import React, { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Upload, X, FileImage, FileText, RefreshCw, CheckCircle2, AlertCircle
} from 'lucide-react';

const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'
];

const MAX_IMAGE_SIZE_MB = 20;
const MAX_PDF_SIZE_MB = 15;

export default function AttachmentUploadModal({
  open,
  onOpenChange,
  jobCardId,
  categories = [],
  defaultCategoryId,
  onUploadComplete,
}) {
  const [files, setFiles] = useState([]);
  const [categoryId, setCategoryId] = useState(defaultCategoryId || '');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleFilesSelected = (selectedFiles) => {
    const validFiles = [];
    const errors = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        return;
      }

      const maxSize = file.type === 'application/pdf' ? MAX_PDF_SIZE_MB : MAX_IMAGE_SIZE_MB;
      if (file.size > maxSize * 1024 * 1024) {
        errors.push(`${file.name}: File too large (max ${maxSize}MB)`);
        return;
      }

      // Create preview for images
      const preview = file.type.startsWith('image/') 
        ? URL.createObjectURL(file) 
        : null;

      validFiles.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview,
        status: 'pending',
        progress: 0,
      });
    });

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFilesSelected(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const uploadFile = async (fileItem) => {
    const formData = new FormData();
    formData.append('file', fileItem.file);
    formData.append('category_id', categoryId);
    if (caption) formData.append('caption', caption);

    try {
      setFiles((prev) =>
        prev.map((f) => (f.id === fileItem.id ? { ...f, status: 'uploading' } : f))
      );

      const response = await api.post(
        `/job-cards/${jobCardId}/attachments`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress((prev) => ({ ...prev, [fileItem.id]: percent }));
          },
        }
      );

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileItem.id ? { ...f, status: 'success', result: response.data } : f
        )
      );

      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Upload failed';
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileItem.id ? { ...f, status: 'error', error: errorMsg } : f
        )
      );
      throw error;
    }
  };

  const handleUploadAll = async () => {
    if (!categoryId) {
      toast.error('Please select a category');
      return;
    }

    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.error('No files to upload');
      return;
    }

    setUploading(true);
    const results = [];

    for (const fileItem of pendingFiles) {
      try {
        const result = await uploadFile(fileItem);
        results.push(result);
      } catch (error) {
        // Continue with other files
      }
    }

    setUploading(false);

    const successCount = results.length;
    const failCount = pendingFiles.length - successCount;

    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully`);
      if (onUploadComplete) onUploadComplete(results);
      
      if (failCount === 0) {
        // All successful - close modal
        handleClose();
      }
    }

    if (failCount > 0) {
      toast.error(`${failCount} file(s) failed to upload`);
    }
  };

  const handleClose = () => {
    // Cleanup previews
    files.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setFiles([]);
    setCaption('');
    setUploadProgress({});
    onOpenChange(false);
  };

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const successCount = files.filter((f) => f.status === 'success').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Upload Attachments</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: cat.color_hex }}
                      />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Drop Zone */}
          <div
            className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-brand-blue/50 hover:bg-slate-50/50 transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">Drop files here or click to browse</p>
            <p className="text-xs text-slate-400 mt-1">
              Images (JPEG, PNG, WebP, HEIC) up to {MAX_IMAGE_SIZE_MB}MB, PDFs up to {MAX_PDF_SIZE_MB}MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_TYPES.join(',')}
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {files.map((fileItem) => (
                <div
                  key={fileItem.id}
                  className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg"
                >
                  {/* Preview */}
                  <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-slate-200">
                    {fileItem.preview ? (
                      <img
                        src={fileItem.preview}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">
                      {fileItem.file.name}
                    </p>
                    <p className="text-xs text-slate-400">
                      {(fileItem.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    {fileItem.status === 'uploading' && (
                      <Progress
                        value={uploadProgress[fileItem.id] || 0}
                        className="h-1 mt-1"
                      />
                    )}
                    {fileItem.status === 'error' && (
                      <p className="text-xs text-red-500 mt-1">{fileItem.error}</p>
                    )}
                  </div>

                  {/* Status / Actions */}
                  <div className="flex-shrink-0">
                    {fileItem.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(fileItem.id);
                        }}
                        className="p-1 hover:bg-slate-200 rounded"
                      >
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    )}
                    {fileItem.status === 'uploading' && (
                      <RefreshCw className="h-4 w-4 text-brand-blue animate-spin" />
                    )}
                    {fileItem.status === 'success' && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                    {fileItem.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Caption */}
          <div className="space-y-2">
            <Label>Caption (optional)</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a note about these files..."
              maxLength={500}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <div className="flex-1 text-sm text-slate-500">
            {files.length > 0 && (
              <span>
                {pendingCount > 0 && `${pendingCount} ready`}
                {successCount > 0 && ` | ${successCount} uploaded`}
                {errorCount > 0 && ` | ${errorCount} failed`}
              </span>
            )}
          </div>
          <Button variant="outline" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadAll}
            disabled={uploading || pendingCount === 0 || !categoryId}
            className="bg-brand-blue hover:bg-brand-blue-dark"
          >
            {uploading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload {pendingCount > 0 ? `(${pendingCount})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
